/**
 * Exporta o desenho do fluxo para um slide .pptx (16:9): título, o canvas como
 * imagem e a legenda das setas. A biblioteca e o rasterizador são carregados
 * sob demanda para não pesar no bundle inicial.
 */

export const ID_CANVAS_FLUXO = "canvas-fluxo";

const LEGENDA: Array<[string, string, boolean]> = [
  ["Movimentação física", "1B6B43", false],
  ["Venda simbólica", "0E7490", true],
  ["Perna automática", "8A927F", false],
];

/**
 * O canvas tem 1040x470 fixos, mas as caixas não têm altura fixa (um terminal
 * de nome longo quebra em várias linhas) e podem terminar além da borda. Na
 * tela isso aparece; na captura, seria cortado. Mede o que de fato existe.
 */
function medirConteudo(alvo: HTMLElement): { largura: number; altura: number } {
  const MARGEM = 12; // sombra e respiro
  const raiz = alvo.getBoundingClientRect();
  const k = raiz.width / alvo.offsetWidth || 1; // escala aplicada por transform
  let largura = alvo.offsetWidth;
  let altura = alvo.offsetHeight;
  for (const el of alvo.querySelectorAll("*")) {
    if (el instanceof SVGSVGElement || el.closest(".nao-imprimir, [data-so-tela]")) continue;
    const r = el.getBoundingClientRect();
    if (!r.width && !r.height) continue;
    largura = Math.max(largura, Math.ceil((r.right - raiz.left) / k) + MARGEM);
    altura = Math.max(altura, Math.ceil((r.bottom - raiz.top) / k) + MARGEM);
  }
  return { largura, altura };
}

export async function exportarFluxoPpt(titulo: string): Promise<void> {
  const alvo = document.getElementById(ID_CANVAS_FLUXO);
  if (!alvo) throw new Error("Desenho do fluxo não encontrado.");

  const [{ default: PptxGenJS }, { toPng }] = await Promise.all([
    import("pptxgenjs"),
    import("html-to-image"),
  ]);
  await document.fonts?.ready;

  const { largura, altura } = medirConteudo(alvo);
  /*
    O `filter` do html-to-image não alcança o interior de um <svg> (ele o clona
    inteiro), então os × dos balões seriam levados junto. Esses ficam ocultos na
    própria tela durante a captura — o estilo vai no clone — e voltam no `finally`.
  */
  const doSvg = Array.from(alvo.querySelectorAll<SVGElement>("svg [data-so-tela]"));
  const anteriores = doSvg.map((el) => el.style.display);
  doSvg.forEach((el) => (el.style.display = "none"));
  let imagem: string;
  try {
    // O canvas vive escalado por `transform`; a captura sai no tamanho nativo.
    imagem = await toPng(alvo, {
      width: largura,
      height: altura,
      pixelRatio: 2,
      style: { transform: "none", background: "none" }, // fundo transparente (sem pontilhado)
      // Como no PDF: sem os × (caixas e balões) e sem a seta dos dropdowns.
      filter: (no) =>
        !(
          no instanceof Element &&
          (no.classList.contains("nao-imprimir") || no.hasAttribute("data-so-tela"))
        ),
    });
  } finally {
    doSvg.forEach((el, i) => (el.style.display = anteriores[i]));
  }

  const pptx = new PptxGenJS();
  pptx.layout = "LAYOUT_WIDE"; // 13,33" x 7,5"
  const slide = pptx.addSlide();
  const margem = 0.5;

  slide.addText(titulo.trim() || "Fluxo da operação", {
    x: margem,
    y: 0.3,
    w: 13.33 - 2 * margem,
    h: 0.6,
    fontFace: "Montserrat",
    fontSize: 24,
    bold: true,
    color: "1F2A1F",
  });

  const areaW = 13.33 - 2 * margem;
  const areaH = 5.4;
  const proporcao = largura / altura;
  let w = areaW;
  let h = w / proporcao;
  if (h > areaH) {
    h = areaH;
    w = h * proporcao;
  }
  slide.addImage({ data: imagem, x: (13.33 - w) / 2, y: 1.05, w, h });

  // Legenda: traço (linha cheia ou tracejada) + rótulo, lado a lado no rodapé.
  const yLegenda = 6.75;
  let x = margem;
  for (const [rotulo, cor, tracejada] of LEGENDA) {
    slide.addShape(pptx.ShapeType.line, {
      x,
      y: yLegenda + 0.15,
      w: 0.45,
      h: 0,
      line: { color: cor, width: 2, dashType: tracejada ? "dash" : "solid" },
    });
    slide.addText(rotulo, {
      x: x + 0.55,
      y: yLegenda,
      w: 2.2,
      h: 0.3,
      fontFace: "Montserrat",
      fontSize: 11,
      color: "55585C",
    });
    x += 3;
  }

  const nomeArquivo = (titulo.trim() || "fluxo").replace(/[\\/:*?"<>|]+/g, "-");
  await pptx.writeFile({ fileName: `${nomeArquivo}.pptx` });
}
